/**
 * DoserneColleagues — contact directory for the Doserne team.
 *
 * Shows every active team member with:
 *  - Full name + role
 *  - Green pulse dot when online (last_seen_at < 3 min ago)
 *  - "Sist sett: 5 min siden" when offline
 *  - Click-to-call phone + click-to-email
 *  - Search box (filters by name/email/phone)
 *
 * Refreshes presence every 60s while the tab is open.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, Phone, Mail, User as UserIcon, Shield, Crown, HardHat, Trash2, MessageCircle, Pencil, X, Save, BadgeCheck, GraduationCap, Award, Bell, BellOff, Send, CheckCircle2, Smartphone, Monitor, RotateCcw } from "lucide-react";
import { Navigate } from "react-router-dom";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Employer loaded-cost rates (Norway / Oslo) — must match backend _loaded_cost.
const FERIE_RATE = 0.12, PENSION_RATE = 0.02, AGA_RATE = 0.141;
const loadedCost = (gross) => {
  const g = Number(gross) || 0;
  if (g <= 0) return 0;
  const base = g + g * FERIE_RATE + g * PENSION_RATE;
  return Math.round(base * (1 + AGA_RATE) * 100) / 100;
};
const fmtKr = (n) => new Intl.NumberFormat("no-NO", { maximumFractionDigits: 0 }).format(n || 0);

function timeAgo(iso) {
  if (!iso) return "aldri";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const min = Math.floor(diff / 60000);
    if (min < 1) return "akkurat nå";
    if (min < 60) return `${min} min siden`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h} time${h === 1 ? "" : "r"} siden`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d} dag${d === 1 ? "" : "er"} siden`;
    return new Date(iso).toLocaleDateString("no-NO");
  } catch { return iso; }
}

const ROLE_META = {
  admin:      { label: "Administrator", Icon: Crown,    color: "text-amber-600 bg-amber-50 border-amber-200", iconColor: "text-amber-600" },
  manager:    { label: "Leder",         Icon: Shield,   color: "text-purple-700 bg-purple-50 border-purple-200", iconColor: "text-purple-700" },
  worker:     { label: "Muncitor",      Icon: HardHat,  color: "text-slate-700 bg-slate-100 border-slate-200", iconColor: "text-slate-500" },
  scaffolder: { label: "Muncitor",      Icon: HardHat,  color: "text-slate-700 bg-slate-100 border-slate-200", iconColor: "text-slate-500" },
  client:     { label: "Client",        Icon: UserIcon, color: "text-slate-500 bg-slate-50 border-slate-200", iconColor: "text-slate-500" },
};
const roleMeta = (r) => ROLE_META[r] || ROLE_META.worker;

const PLATFORM_META = {
  android: { label: "Android", Icon: Smartphone, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  ios:     { label: "iOS",     Icon: Smartphone, color: "text-slate-700 bg-slate-100 border-slate-300" },
  web:     { label: "Web",     Icon: Monitor,    color: "text-blue-700 bg-blue-50 border-blue-200" },
};

function ColleagueRow({ c, isAdmin, onHide, onToggleAdmin, onToggleMute, onChat, onEdit, onViewCourses }) {
  const meta = roleMeta(c.role);
  const RoleIcon = meta.Icon;
  const initials = (c.full_name || c.email).split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const tel = c.phone ? c.phone.replace(/\s+/g, "") : "";
  const statusText = c.is_online ? "Online" : c.last_seen_at ? timeAgo(c.last_seen_at) : (c.activated ? "Har logget inn" : "Ikke innlogget");
  const canHide = isAdmin && !c.is_me;
  // Platform-role admins (holding_admin, operations_manager, platform_admin)
  // can't have the flag toggled — their role already grants admin rights.
  const hasBuiltinAdminRole = ["platform_admin", "operations_manager", "holding_admin"].includes(c.role);
  const canToggleAdmin = isAdmin && !c.is_me && !hasBuiltinAdminRole;
  const canEdit = isAdmin;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 transition ${c.is_me ? "bg-cyan-50/60 border-cyan-100" : "bg-white border-slate-100 hover:bg-slate-50"}`}
      data-testid={`colleague-${c.email}`}
    >
      {/* Avatar with presence dot */}
      <div className="relative flex-shrink-0">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-white text-[11px] ${c.is_online ? "bg-emerald-500" : "bg-slate-400"}`}>
          {initials || "??"}
        </div>
        {c.is_online && (
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
        )}
      </div>

      {/* Name + role + status */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-black text-slate-900 truncate">{c.full_name}</span>
          {c.is_me && <span className="text-[9px] uppercase tracking-widest text-cyan-700 font-black flex-shrink-0">(deg)</span>}
          <RoleIcon className={`w-3 h-3 flex-shrink-0 ${meta.iconColor}`} />
          {c.is_admin && (
            <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5 flex-shrink-0" title="Doserne Academy admin">
              <Crown className="w-2.5 h-2.5" /> Admin
            </span>
          )}
          {c.notifications_muted && (
            <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-black text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 flex-shrink-0" title={c.mute_until ? `Varsler av til ${c.mute_until}` : "Varsler av (ferie)"} data-testid={`muted-badge-${c.email}`}>
              <BellOff className="w-2.5 h-2.5" /> {c.mute_until ? `Ferie → ${c.mute_until}` : "Ferie"}
            </span>
          )}
          {c.passed_courses && c.passed_courses.length > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-black bg-emerald-500 rounded-full px-1.5 py-0.5 flex-shrink-0 leading-none"
              style={{ color: "#fff" }}
              title={`Fullførte kurs: ${c.passed_courses.length}`}
              data-testid={`passed-courses-${c.email}`}
            >
              <BadgeCheck className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={2.8} />
              <span className="tracking-wide">{c.passed_courses.map((p) => p.emoji).join(" ")}</span>
            </span>
          )}
          {c.qualifications && c.qualifications.length > 0 && c.qualifications.map((qi, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-1.5 py-0.5 flex-shrink-0 leading-none"
              title={qi.label}
              data-testid={`qual-badge-${c.email}-${i}`}
            >
              <span>{qi.emoji}</span>
              <span className="tracking-wide">{qi.label}</span>
            </span>
          ))}
          {c.is_online && c.platform && PLATFORM_META[c.platform] && (() => {
            const pm = PLATFORM_META[c.platform];
            const PIcon = pm.Icon;
            return (
              <span
                className={`inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-black border rounded-full px-1.5 py-0.5 flex-shrink-0 ${pm.color}`}
                title={`Aktiv på ${pm.label}`}
                data-testid={`platform-${c.email}`}
              >
                <PIcon className="w-2.5 h-2.5" /> {pm.label}
              </span>
            );
          })()}
        </div>
        <div className={`text-[11px] truncate ${c.is_online ? "text-emerald-600 font-semibold" : "text-slate-500"}`}>
          {c.is_online ? "🟢 " : ""}{statusText}
          {c.phone ? (
            <span className="text-slate-400 ml-1.5">· {c.phone}</span>
          ) : canEdit && (
            <button type="button"
                    onClick={() => onEdit(c)}
                    className="text-amber-600 hover:text-amber-800 ml-1.5 font-semibold underline decoration-dotted"
                    title="Legg til telefonnummer"
                    data-testid={`add-phone-${c.email}`}>
              + Legg til telefon
            </button>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {!c.is_me && (
          <button
            type="button"
            onClick={() => onChat(c)}
            className="w-8 h-8 rounded-lg bg-[#0084ff] hover:bg-[#0074e1] text-white flex items-center justify-center"
            title="Send melding"
            data-testid={`chat-${c.email}`}
          >
            <MessageCircle className="w-3.5 h-3.5" />
          </button>
        )}
        {tel && (
          <a href={`tel:${tel}`} className="w-8 h-8 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center" title="Ring" data-testid={`call-${c.email}`}>
            <Phone className="w-3.5 h-3.5" />
          </a>
        )}
        {tel && (
          <a href={`sms:${tel}`} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center text-sm" title="SMS" data-testid={`sms-${c.email}`}>💬</a>
        )}
        <a href={`mailto:${c.email}`} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center" title="E-post" data-testid={`mail-${c.email}`}>
          <Mail className="w-3.5 h-3.5" />
        </a>
        {canEdit && (
          <button
            type="button"
            onClick={() => onViewCourses(c)}
            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-indigo-100 text-slate-600 hover:text-indigo-700 flex items-center justify-center border border-slate-200"
            title="Kurs og diplomer"
            data-testid={`view-courses-${c.email}`}
          >
            <GraduationCap className="w-3.5 h-3.5" />
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={() => onEdit(c)}
            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-cyan-100 text-slate-600 hover:text-cyan-700 flex items-center justify-center border border-slate-200"
            title="Rediger profil"
            data-testid={`edit-${c.email}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={() => onToggleMute(c)}
            className={`w-8 h-8 rounded-lg flex items-center justify-center border ${c.notifications_muted ? "bg-amber-50 hover:bg-amber-100 text-amber-600 border-amber-200" : "bg-slate-50 hover:bg-slate-100 text-slate-400 border-slate-200"}`}
            title={c.notifications_muted ? "Slå på varsler (avslutt ferie-modus)" : "Slå av varsler (ferie)"}
            data-testid={`toggle-mute-${c.email}`}
          >
            {c.notifications_muted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
          </button>
        )}
        {canToggleAdmin && (
          <button
            type="button"
            onClick={() => onToggleAdmin(c)}
            className={`w-8 h-8 rounded-lg flex items-center justify-center border ${c.is_admin ? "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200" : "bg-slate-50 hover:bg-slate-100 text-slate-400 border-slate-200"}`}
            title={c.is_admin ? "Fjern Doserne Admin-rettigheter" : "Gjør til Doserne Admin"}
            data-testid={`toggle-admin-${c.email}`}
          >
            <Crown className="w-3.5 h-3.5" />
          </button>
        )}
        {canHide && (
          <button
            type="button"
            onClick={() => onHide(c)}
            className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center border border-red-200"
            title="Skjul fra Kolleger + fjern tilgang"
            data-testid={`hide-${c.email}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function DoserneColleagues() {
  const { user, loading, authHeader } = useDoserneAuth();
  const [colleagues, setColleagues] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // colleague object being edited
  const [coursesFor, setCoursesFor] = useState(null); // colleague whose courses/diplomas are shown

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/colleagues`, authHeader());
      setColleagues(data.colleagues || []);
    } catch {
      setColleagues([]);
    }
  }, [authHeader]);

  const handleHide = useCallback(async (c) => {
    const label = c.full_name || c.email;
    if (!window.confirm(
      `Skjul ${label} fra Kolleger?\n\n` +
      `• Fjernes fra Kolleger-fanen\n` +
      `• Mister tilgang til Doserne Academy\n` +
      `• Alt historisk arbeid (bilder, planer, opplastinger) bevares\n\n` +
      `Kan reaktiveres senere ved å invitere på nytt.`
    )) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/doserne/colleagues/${encodeURIComponent(c.email)}/hide`,
        {},
        authHeader(),
      );
      setColleagues((cur) => (cur || []).filter((x) => x.email !== c.email));
    } catch (e) {
      window.alert(`Kunne ikke skjule kollega: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }, [authHeader]);

  const handleToggleAdmin = useCallback(async (c) => {
    const target = !c.is_admin;
    const label = c.full_name || c.email;
    const msg = target
      ? `Gi ${label} full Doserne Academy admin-tilgang?\n\nDe vil kunne:\n• Opprette og redigere daglige planer\n• Legge til / arkivere prosjekter\n• Kjøre Tripletex-synk\n• Invitere og skjule kolleger`
      : `Fjerne admin-rettigheter fra ${label}?\n\nDe beholder tilgang til Doserne Academy som vanlig medlem.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/doserne/colleagues/${encodeURIComponent(c.email)}/set-admin`,
        { is_admin: target },
        authHeader(),
      );
      setColleagues((cur) => (cur || []).map((x) => x.email === c.email ? { ...x, is_admin: target } : x));
    } catch (e) {
      window.alert(`Kunne ikke endre admin-status: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }, [authHeader]);

  const handleToggleMute = useCallback(async (c) => {
    const target = !c.notifications_muted;
    // Optimistic — the mute toggle is low-stakes and instant feedback matters.
    setColleagues((cur) => (cur || []).map((x) => x.email === c.email ? { ...x, notifications_muted: target, mute_until: "" } : x));
    try {
      await axios.put(
        `${API}/doserne/admin/users/${encodeURIComponent(c.email)}/profile`,
        { notifications_muted: target, mute_until: "" },
        authHeader(),
      );
      toast.success(target ? `🔕 Varsler av for ${c.full_name || c.email} (ferie)` : `🔔 Varsler på igjen for ${c.full_name || c.email}`);
    } catch (e) {
      setColleagues((cur) => (cur || []).map((x) => x.email === c.email ? { ...x, notifications_muted: !target } : x));
      toast.error(`Kunne ikke endre varsler: ${e?.response?.data?.detail || e.message}`);
    }
  }, [authHeader]);

  const handleChat = useCallback((c) => {
    // Dispatch a global event — ChatFab (mounted in DoserneShell)
    // listens for it and opens a floating window on desktop or
    // navigates to /doserne/chat?room=... on mobile.
    window.dispatchEvent(new CustomEvent("doserne:open-dm", { detail: { email: c.email } }));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    // Refresh every 60s so presence stays fresh
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [user, load]);

  const filtered = useMemo(() => {
    if (!colleagues) return [];
    const term = q.trim().toLowerCase();
    if (!term) return colleagues;
    return colleagues.filter((c) => {
      const hay = [c.full_name, c.email, c.phone].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [colleagues, q]);

  if (loading) return <DoserneShell><div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></DoserneShell>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  const onlineCount = filtered.filter((c) => c.is_online).length;

  return (
    <DoserneShell>
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Kolleger</h1>
          <p className="text-sm text-slate-500 mt-1">Kontakt teamet ditt — ring, send SMS eller e-post direkte.</p>
        </div>
        {colleagues && (
          <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5" data-testid="online-badge">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            {onlineCount} online nå
          </div>
        )}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Søk navn, e-post, telefon..."
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 text-sm text-slate-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
          data-testid="colleagues-search"
        />
      </div>

      {colleagues === null ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-500 italic">
          Ingen kolleger funnet.
        </div>
      ) : (
        <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden ${busy ? "opacity-70 pointer-events-none" : ""}`} data-testid="colleagues-list">
          {filtered.map((c) => <ColleagueRow key={c.email} c={c} isAdmin={!!user?.is_admin} onHide={handleHide} onToggleAdmin={handleToggleAdmin} onToggleMute={handleToggleMute} onChat={handleChat} onEdit={setEditing} onViewCourses={setCoursesFor} />)}
        </div>
      )}

      {editing && (
        <AdminEditProfileModal
          colleague={editing}
          authHeader={authHeader}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setColleagues((cur) => (cur || []).map((x) => x.email === updated.email ? { ...x, ...updated } : x));
            setEditing(null);
          }}
        />
      )}

      {coursesFor && (
        <ColleagueCoursesModal
          colleague={coursesFor}
          authHeader={authHeader}
          onClose={() => setCoursesFor(null)}
        />
      )}
    </DoserneShell>
  );
}

// -----------------------------------------------------------------------------
// AdminEditProfileModal — admin-only editor for any Doserne user's profile.
// Loads the full user record (except secrets) via GET, submits partial
// changes via PUT. Handles the common "fix Bogdan's missing phone" case.
// -----------------------------------------------------------------------------
function AdminEditProfileModal({ colleague, authHeader, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    full_name: "", phone: "", role: "worker", is_doserne_admin: false,
    is_active: true, address: "", birth_date: "", personnummer: "", platform_access_from: "",
    mute_until: "", hourly_cost: "", gross_salary: "", termination_date: "", hidden_from_team: false, bank_account: "",
  });
  const [initialMuteUntil, setInitialMuteUntil] = useState("");
  const [quals, setQuals] = useState([]); // [{label, emoji}]

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/doserne/admin/users/${encodeURIComponent(colleague.email)}`,
          authHeader(),
        );
        if (!alive) return;
        const u = data.user || {};
        setForm({
          full_name: u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim(),
          phone: u.phone || "",
          role: u.role || "worker",
          is_doserne_admin: !!u.is_doserne_admin,
          is_active: u.is_active !== false,
          address: u.address || "",
          birth_date: u.birth_date || "",
          personnummer: u.personnummer || "",
          platform_access_from: (u.platform_access_from || "").slice(0, 10),
          mute_until: (u.mute_until || "").slice(0, 10),
          hourly_cost: (u.hourly_cost != null && u.hourly_cost !== "") ? String(u.hourly_cost) : "",
          gross_salary: (u.gross_salary != null && u.gross_salary !== "") ? String(u.gross_salary) : "",
          termination_date: (u.termination_date || "").slice(0, 10),
          hidden_from_team: !!u.hidden_from_team,
          bank_account: u.bank_account || "",
        });
        setInitialMuteUntil((u.mute_until || "").slice(0, 10));
        setQuals(Array.isArray(u.qualifications) ? u.qualifications.map((q) => ({ label: q.label || "", emoji: q.emoji || "" })) : []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Kunne ikke laste bruker");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [colleague.email, authHeader]);

  const setField = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setSaving(true);
    try {
      const payload = { ...form };
      // Loaded employer cost is DERIVED from gross salary (+ feriepenger,
      // pension, arbeidsgiveravgift Oslo) — kept in sync for display.
      const gs = Number(payload.gross_salary) || 0;
      if (payload.gross_salary === "" || payload.gross_salary == null) {
        delete payload.gross_salary;
        delete payload.hourly_cost;
      } else {
        payload.gross_salary = gs;
        payload.hourly_cost = loadedCost(gs);
      }
      // Only touch mute fields when the vacation end-date actually changed —
      // otherwise we'd clobber an indefinite (dateless) mute set via the bell.
      if (form.mute_until === initialMuteUntil) {
        delete payload.mute_until;
      } else {
        payload.notifications_muted = !!form.mute_until;
      }
      await axios.put(
        `${API}/doserne/admin/users/${encodeURIComponent(colleague.email)}/profile`,
        payload,
        authHeader(),
      );
      const cleanQuals = quals
        .map((q) => ({ label: (q.label || "").trim(), emoji: (q.emoji || "").trim() }))
        .filter((q) => q.label && q.emoji);
      await axios.put(
        `${API}/doserne/admin/users/${encodeURIComponent(colleague.email)}/qualifications`,
        { qualifications: cleanQuals },
        authHeader(),
      );
      onSaved({ email: colleague.email, full_name: form.full_name, phone: form.phone, role: form.role, is_admin: form.is_doserne_admin, qualifications: cleanQuals });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kunne ikke lagre endringer");
    } finally {
      setSaving(false);
    }
  };

  const label = "block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1";
  const input = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500";

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="admin-edit-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col"
           onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-700">Admin</div>
            <h2 className="text-lg font-black text-slate-900">Rediger profil</h2>
            <div className="text-xs text-slate-500 truncate">{colleague.email}</div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900" data-testid="admin-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline-block" /></div>
        ) : (
          <form onSubmit={save} className="p-5 space-y-4 overflow-y-auto">
            <div>
              <label className={label}>Fullt navn</label>
              <input className={input} value={form.full_name} onChange={setField("full_name")} data-testid="edit-full-name" />
            </div>

            <div>
              <label className={label}>Telefon <span className="text-amber-600 normal-case tracking-normal">(brukes for Ring / SMS)</span></label>
              <input className={input} value={form.phone} onChange={setField("phone")} placeholder="+47 123 45 678" data-testid="edit-phone" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Rolle</label>
                <select className={input} value={form.role} onChange={setField("role")} data-testid="edit-role">
                  <option value="worker">Muncitor / Scaffolder</option>
                  <option value="scaffolder">Scaffolder</option>
                  <option value="manager">Leder</option>
                  <option value="admin">Administrator</option>
                  <option value="client">Client</option>
                </select>
              </div>
              <div>
                <label className={label}>Aktiv</label>
                <label className="flex items-center gap-2 text-sm text-slate-700 h-[38px] px-2">
                  <input type="checkbox" checked={form.is_active} onChange={setField("is_active")} className="w-4 h-4" data-testid="edit-active" />
                  Kan logge inn
                </label>
              </div>
            </div>

            <div>
              <label className={label}>Doserne Academy admin</label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.is_doserne_admin} onChange={setField("is_doserne_admin")} className="w-4 h-4" data-testid="edit-doserne-admin" />
                Kan opprette daglige planer, redigere prosjekter, invitere kolleger
              </label>
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">HR-info (valgfritt)</div>
              <div>
                <label className={label}>Adresse</label>
                <input className={input} value={form.address} onChange={setField("address")} data-testid="edit-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>Fødselsdato</label>
                  <input type="date" className={input} value={form.birth_date} onChange={setField("birth_date")} data-testid="edit-birth-date" />
                </div>
                <div>
                  <label className={label}>Personnummer</label>
                  <input className={input} value={form.personnummer} onChange={setField("personnummer")} placeholder="11 siffer" data-testid="edit-personnummer" />
                </div>
              </div>
              <div>
                <label className={label}>Aktiv på plattform fra <span className="text-cyan-600 normal-case tracking-normal">(til da: kun kurs)</span></label>
                <input type="date" className={input} value={form.platform_access_from} onChange={setField("platform_access_from")} data-testid="edit-platform-access-from" />
                <p className="text-[11px] text-slate-400 mt-1">Tom = full tilgang nå. Settes automatisk fra kontraktens startdato.</p>
              </div>
              <div>
                <label className={label}>Kostnad per time <span className="text-emerald-600 normal-case tracking-normal">(auto — cost real angajator/oră)</span></label>
                <div className="w-full px-3 py-2.5 border border-slate-200 bg-slate-50 rounded-lg text-sm" data-testid="edit-hourly-cost-auto">
                  {Number(form.gross_salary) > 0 ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="font-black text-slate-900">{fmtKr(loadedCost(form.gross_salary))} kr/t</span>
                        <span className="text-[10px] text-slate-400">calculat automat</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                        {fmtKr(Number(form.gross_salary))} brut + feriepenger 12% + pensjon 2% + arbeidsgiveravgift Oslo 14,1%
                      </div>
                    </>
                  ) : (
                    <span className="text-slate-400 text-[13px]">Setează salariul brut/oră mai jos → costul se calculează automat.</span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Folosit pentru bugetul pe proiecte și coloana Cost din Pontaj.</p>
              </div>
              <div>
                <label className={label}>Salariu brut / oră <span className="text-indigo-600 normal-case tracking-normal">(Timelønn — folosit la fișa de salariu)</span></label>
                <div className="relative">
                  <input type="number" min="0" step="1" inputMode="decimal" className={input} value={form.gross_salary} onChange={setField("gross_salary")} placeholder="f.eks. 290" data-testid="edit-gross-salary" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">kr/t</span>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Salariul brut orar. Overtime 50% = 1.5×, overtime 100% (zile roșii/duminici) = 2×.</p>
              </div>
              <div>
                <label className={label}>Ferie til <span className="text-amber-600 normal-case tracking-normal">(varsler av til denne datoen)</span></label>
                <input type="date" className={input} value={form.mute_until} onChange={setField("mute_until")} data-testid="edit-mute-until" />
                <p className="text-[11px] text-slate-400 mt-1">Varsler skrus av til og med denne datoen, og slås på igjen automatisk. Tom = ingen planlagt ferie.</p>
              </div>
              <div>
                <label className={label}>Sluttdato <span className="text-red-600 normal-case tracking-normal">(mister tilgang etter denne datoen)</span></label>
                <input type="date" className={input} value={form.termination_date} onChange={setField("termination_date")} data-testid="edit-termination-date" />
                <p className="text-[11px] text-slate-400 mt-1">Fra og med denne datoen kan brukeren ikke logge inn, og aktive økter stoppes. Data beholdes. Tom = ingen sluttdato.</p>
              </div>
              <div>
                <label className="flex items-center gap-3 cursor-pointer py-1">
                  <input type="checkbox" className="w-4 h-4 accent-cyan-600" checked={!!form.hidden_from_team} onChange={setField("hidden_from_team")} data-testid="edit-hidden-from-team" />
                  <span className="text-sm font-semibold text-slate-700">Skjul for kolleger <span className="text-slate-400 font-normal">(kun admin ser denne personen)</span></span>
                </label>
                <p className="text-[11px] text-slate-400 mt-1">Personen vises ikke i kollegalisten/chat/anrop for vanlige medlemmer. Administratorer ser alle.</p>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Insignier / ekstra ferdigheter</div>
                <button
                  type="button"
                  onClick={() => setQuals((q) => [...q, { emoji: "", label: "" }])}
                  className="text-xs font-bold text-cyan-700 hover:text-cyan-900"
                  data-testid="qual-add"
                >
                  + Legg til
                </button>
              </div>
              <p className="text-[11px] text-slate-400 -mt-1">F.eks. 🏗️ Kranfører, 🚛 Sjåfør. Synlig for alle kolleger.</p>
              {quals.length === 0 ? (
                <div className="text-[11px] text-slate-400 italic py-1">Ingen insignier ennå.</div>
              ) : (
                quals.map((qi, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`qual-row-${idx}`}>
                    <input
                      className="w-14 px-2 py-2 border border-slate-300 rounded-lg text-center text-lg"
                      value={qi.emoji}
                      onChange={(e) => setQuals((q) => q.map((x, i) => i === idx ? { ...x, emoji: e.target.value } : x))}
                      placeholder="🏗️"
                      maxLength={8}
                      data-testid={`qual-emoji-${idx}`}
                    />
                    <input
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      value={qi.label}
                      onChange={(e) => setQuals((q) => q.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                      placeholder="Kranfører"
                      maxLength={40}
                      data-testid={`qual-label-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => setQuals((q) => q.filter((_, i) => i !== idx))}
                      className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0"
                      data-testid={`qual-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {err && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{err}</div>}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100" data-testid="edit-cancel">
                Avbryt
              </button>
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-sm font-semibold" style={{ color: "#fff" }} data-testid="edit-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Lagre endringer
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// ColleagueCoursesModal — admin view of a colleague's completed courses and
// their diplomas. Each finished course with a certificate can be opened in an
// in-app diploma viewer (nothing opens in an external tab).
// -----------------------------------------------------------------------------
function pickText(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v.no || v.ro || v.en || Object.values(v)[0] || "";
}

const REMINDER_DEFAULT = "📚 Påminnelse: du har kurs som ikke er fullført i Doserne Academy. Fullfør dem når du får tid!";

function ColleagueCoursesModal({ colleague, authHeader, onClose }) {
  const [courses, setCourses] = useState(null);
  const [diploma, setDiploma] = useState(null); // { title, html } | "loading"
  const [reminderText, setReminderText] = useState(REMINDER_DEFAULT);
  const [remindState, setRemindState] = useState("idle"); // idle | sending | sent
  const [granting, setGranting] = useState("");
  const [resetState, setResetState] = useState("idle"); // idle | resetting | done

  const resetCourses = async () => {
    if (resetState === "resetting") return;
    if (!window.confirm(
      `Resetează TOATE cursurile pentru ${colleague.full_name || colleague.email}?\n\nSe șterge TOT: progresul, diplomele/certificatele emise și semnăturile. Colegul reface cursurile de la zero.`
    )) return;
    setResetState("resetting");
    try {
      await axios.post(
        `${API}/doserne/admin/users/${encodeURIComponent(colleague.email)}/reset-courses`,
        {},
        authHeader(),
      );
      // Full reset — clear progress AND diplomas/certificates in the UI too.
      setCourses((cur) => (cur || []).map((c) => ({
        ...c, completed: false, score: undefined, granted: false, cert_id: undefined,
      })));
      // Re-fetch to reflect the true server state.
      try {
        const { data } = await axios.get(
          `${API}/doserne/colleagues/${encodeURIComponent(colleague.email)}/courses`, authHeader());
        setCourses(data.courses || []);
      } catch { /* keep optimistic */ }
      setResetState("done");
      setTimeout(() => setResetState("idle"), 2500);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Nu s-au putut reseta cursurile.");
      setResetState("idle");
    }
  };

  useEffect(() => {
    axios.get(`${API}/doserne/colleagues/${encodeURIComponent(colleague.email)}/courses`, authHeader())
      .then(({ data }) => setCourses(data.courses || []))
      .catch(() => setCourses([]));
  }, [colleague.email, authHeader]);

  const grantCourse = async (courseCode, title) => {
    if (granting) return;
    if (!window.confirm(`Registrer „${title || courseCode}" som fullført for ${colleague.full_name || colleague.email}? Det utstedes et admin-signert diplom som viser at kurset ble tatt før appen.`)) return;
    setGranting(courseCode);
    try {
      const { data } = await axios.post(
        `${API}/doserne/admin/grant-course`,
        { email: colleague.email, course_code: courseCode },
        authHeader(),
      );
      const cert = data.certificate || {};
      setCourses((cur) => (cur || []).map((c) => c.course_code === courseCode
        ? { ...c, completed: true, cert_id: cert.id, cert_number: cert.cert_number, issued_at: cert.issued_at, score: undefined, granted: true }
        : c));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Kunne ikke registrere kurset.");
    } finally {
      setGranting("");
    }
  };

  const sendReminder = async () => {
    if (remindState === "sending" || !reminderText.trim()) return;
    setRemindState("sending");
    try {
      await axios.post(
        `${API}/doserne/admin/colleagues/${encodeURIComponent(colleague.email)}/kurs-reminder`,
        { message: reminderText.trim() },
        authHeader(),
      );
      setRemindState("sent");
    } catch {
      setRemindState("idle");
      window.alert("Kunne ikke sende påminnelse.");
    }
  };

  const viewDiploma = async (certId, title) => {
    setDiploma("loading");
    try {
      const { data } = await axios.get(`${API}/doserne/certificates/${certId}/view`, authHeader());
      setDiploma({ title, html: data.html || "" });
    } catch {
      setDiploma(null);
      window.alert("Kunne ikke åpne diplomet.");
    }
  };

  const done = (courses || []).filter((c) => c.completed).length;

  return (
    <div className="fixed inset-0 z-[9998] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="colleague-courses-modal">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="min-w-0">
            <div className="font-black text-slate-900 truncate">{colleague.full_name || colleague.email}</div>
            <div className="text-xs text-slate-500">Kurs og diplomer{courses ? ` · ${done}/${courses.length}` : ""}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center flex-shrink-0" data-testid="colleague-courses-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {courses === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : courses.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-400 italic">Ingen kurs.</div>
          ) : (
            courses.map((c) => (
              <div key={c.course_code} className={`flex items-center gap-2.5 p-2.5 rounded-lg border ${c.completed ? "border-emerald-200 bg-emerald-50" : "border-slate-100 bg-slate-50"}`} data-testid={`colleague-course-${c.course_code}`}>
                <span className="text-xl" style={{ opacity: c.completed ? 1 : 0.35 }}>{c.cover_emoji || "📘"}</span>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-bold truncate ${c.completed ? "text-slate-900" : "text-slate-400"}`}>{pickText(c.course_title) || c.course_code}</div>
                  {c.completed && (
                    <div className="text-[11px] text-emerald-700">{c.cert_number ? `${c.cert_number} · ` : ""}{(c.issued_at || "").slice(0, 10)}{typeof c.score === "number" ? ` · ${Math.round(c.score <= 1 ? c.score * 100 : c.score)}%` : ""}</div>
                  )}
                </div>
                {c.completed && c.cert_id ? (
                  <button
                    onClick={() => viewDiploma(c.cert_id, pickText(c.course_title))}
                    className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-2.5 py-1.5 rounded-full flex-shrink-0"
                    data-testid={`colleague-diploma-${c.course_code}`}
                  >
                    <Award className="w-3.5 h-3.5" /> Diplom
                  </button>
                ) : c.completed ? (
                  <BadgeCheck className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                ) : (
                  <button
                    onClick={() => grantCourse(c.course_code, pickText(c.course_title))}
                    disabled={!!granting}
                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold px-2.5 py-1.5 rounded-full flex-shrink-0"
                    title="Registrer som fullført (papirbevis)"
                    data-testid={`colleague-grant-${c.course_code}`}
                  >
                    {granting === c.course_code ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><GraduationCap className="w-3.5 h-3.5" /> Gi kurs</>}
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {courses && courses.some((c) => !c.completed) && (
          <div className="border-t border-slate-200 bg-indigo-50/60 p-3" data-testid="colleague-reminder-card">
            <div className="flex items-center gap-1.5 mb-2">
              <Bell className="w-3.5 h-3.5 text-indigo-600" />
              <span className="text-[11px] font-black uppercase tracking-widest text-indigo-700">Send påminnelse</span>
            </div>
            <textarea
              value={reminderText}
              onChange={(e) => { setReminderText(e.target.value); if (remindState === "sent") setRemindState("idle"); }}
              rows={3}
              className="w-full text-sm text-slate-800 border border-slate-300 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder={REMINDER_DEFAULT}
              data-testid="colleague-reminder-input"
            />
            <button
              type="button"
              onClick={sendReminder}
              disabled={remindState !== "idle" || !reminderText.trim()}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-bold px-3 py-2.5 rounded-lg"
              data-testid="colleague-reminder-send"
            >
              {remindState === "sending" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : remindState === "sent" ? (
                <><CheckCircle2 className="w-4 h-4" /> Påminnelse sendt</>
              ) : (
                <><Send className="w-4 h-4" /> Send påminnelse</>
              )}
            </button>
          </div>
        )}

        <div className="border-t border-slate-200 p-3">
          <button
            type="button"
            onClick={resetCourses}
            disabled={resetState === "resetting"}
            className="w-full inline-flex items-center justify-center gap-2 bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 text-sm font-bold px-3 py-2.5 rounded-lg"
            data-testid="colleague-reset-courses"
          >
            {resetState === "resetting" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : resetState === "done" ? (
              <><CheckCircle2 className="w-4 h-4" /> Cursuri resetate</>
            ) : (
              <><RotateCcw className="w-4 h-4" /> Resetează cursurile</>
            )}
          </button>
          <div className="text-[11px] text-slate-400 text-center mt-1.5">Diplomele emise se păstrează</div>
        </div>
      </div>

      {diploma && (
        <DiplomaViewer diploma={diploma} onClose={() => setDiploma(null)} />
      )}
    </div>
  );
}

// In-app diploma viewer — renders the certificate HTML in a sandboxed iframe.
export function DiplomaViewer({ diploma, onClose }) {
  return (
    <div className="fixed inset-0 z-[10000] bg-black/80 flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="diploma-viewer">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="truncate font-bold text-sm pr-3">{diploma === "loading" ? "..." : (diploma.title || "Diplom")}</div>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center flex-shrink-0" data-testid="diploma-viewer-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 min-h-0 p-2">
        {diploma === "loading" ? (
          <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-white" /></div>
        ) : (
          <iframe title="Diplom" srcDoc={diploma.html} className="w-full h-full bg-white rounded" data-testid="diploma-viewer-frame" />
        )}
      </div>
    </div>
  );
}
